import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { IoClose, IoShieldCheckmarkOutline, IoTrashOutline, IoWarningOutline } from 'react-icons/io5';
import { RoleDefinition } from 'src/types/permissions.types';

interface DeleteRoleModalProps {
  open: boolean;
  role: RoleDefinition | null;
  memberCount: number;
  onClose: () => void;
  onConfirm: () => Promise<unknown>;
  isDeleting?: boolean;
}

/** Same layout as DeleteCohortModal: impact first, then an explicit acknowledgment. */
export function DeleteRoleModal({ open, role, memberCount, onClose, onConfirm, isDeleting }: DeleteRoleModalProps) {
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (open) setAcknowledged(false);
  }, [open]);

  if (!role) return null;
  const blocked = memberCount > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ px: 3, pt: 2.5, pb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#FEE2E2', color: '#DC2626', display: 'grid', placeItems: 'center' }}>
              <IoWarningOutline size={22} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={800} color="#0F172A">
                Delete Role
              </Typography>
              <Typography variant="caption" color="text.secondary">
                You are about to permanently delete <strong>{role.label}</strong> ({role.name}).
              </Typography>
            </Box>
          </Stack>
          <IconButton size="small" onClick={onClose} disabled={isDeleting} aria-label="Close" sx={{ color: '#94A3B8' }}>
            <IoClose size={20} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider sx={{ borderColor: '#F1F5F9' }} />

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {blocked ? (
          <Box sx={{ p: 1.75, borderRadius: 2, bgcolor: '#FFFBEB', border: '1px solid #FDE68A' }}>
            <Typography variant="subtitle2" fontWeight={800} color="#92400E" fontSize="0.8125rem">
              {memberCount} {memberCount === 1 ? 'person still has' : 'people still have'} this role
            </Typography>
            <Typography variant="caption" color="#B45309" fontSize="0.75rem" sx={{ display: 'block', mt: 0.25 }}>
              Move them to another role from the Members tab first. A role cannot be deleted while anyone depends on it, so nobody
              is ever locked out.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2.5}>
            <Box sx={{ p: 1.75, borderRadius: 2, bgcolor: '#F0FDF4', border: '1px solid #BBF7D0' }}>
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <Box sx={{ color: '#16A34A', mt: 0.25 }}>
                  <IoShieldCheckmarkOutline size={20} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={800} color="#166534" fontSize="0.8125rem">
                    Nobody is affected
                  </Typography>
                  <Typography variant="caption" color="#15803D" fontSize="0.75rem" sx={{ display: 'block', mt: 0.25 }}>
                    No one has this role, and no records are deleted with it.
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#FEF2F2', border: '1px solid #FECACA' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                    sx={{ color: '#DC2626', '&.Mui-checked': { color: '#DC2626' }, py: 0.5 }}
                  />
                }
                label={
                  <Typography variant="body2" color="#991B1B" fontWeight={600} fontSize="0.8125rem">
                    I understand that deleting <strong>{role.label}</strong> cannot be undone.
                  </Typography>
                }
                sx={{ m: 0, alignItems: 'flex-start' }}
              />
            </Box>
          </Stack>
        )}
      </DialogContent>

      <Divider sx={{ borderColor: '#F1F5F9' }} />

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button onClick={onClose} disabled={isDeleting} sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.8125rem', textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={blocked || !acknowledged || isDeleting}
          onClick={onConfirm}
          startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <IoTrashOutline size={16} />}
          sx={{
            fontWeight: 700,
            fontSize: '0.8125rem',
            px: 2.5,
            py: 0.8,
            borderRadius: 2,
            textTransform: 'none',
            bgcolor: '#DC2626',
            boxShadow: '0 2px 8px rgba(220, 38, 38, 0.25)',
            '&:hover': { bgcolor: '#B91C1C' },
          }}
        >
          {isDeleting ? 'Deleting Role...' : `Permanently Delete ${role.label}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
